// @ts-strict-ignore
import { getAllowedAttributesForPortfolioWorkflow } from '~/flow/utils';
import type { Attribute } from '~/flow/types';

import {
  ActionType,
  MatrixModelDataType,
} from '../constants';
import { extractThresholdIds } from '../../../service/thresholds/thresholds';
import { addThresholdToBooleanRule, deleteThresholdFromBooleanRule } from './rules/rules';
import type {
  ServicesByName,
  Action,
  SchemaInstance,
  ServiceObject,
  Threshold,
  ThresholdLeft,
  ThresholdRight,
  WorkflowInfo,
  WorkflowProps,
} from '../types';
import { transformMLAAttributeComponents } from '../transformation/thresholds';
import {
  getDefaultRightAttributeValue,
  getDefaultAction,
  getDefaultAttribute,
  getDefaultReasonCodeGroup,
  getOperatorAndRightAttrValues,
  getDefaultMLAAttributeComponents,
  resolveAggregationComponents,
  assignDefaultsToMLAFilterComponents,
  getAttribute,
} from './helpers';
import {
  isAggregationServiceAction,
} from '../../../service/services';
import {
  getActionDataType,
  getAllowedAttributesWithMatchingDataType,
  getFormattedMatrixDataType,
  isActionOfType,
} from '../../../service/actions';
import { setProperty } from 'dot-prop';
import type { WorkflowState } from 'flow/store/redux/state';
import { generatePublicId } from '../utils';
import {
  defaultOperatorByActionType,
  defaultOperatorByDataType,
  emptyThresholdLeft,
  emptyThresholdRight,
} from './consts';

function generateThresholdFromThreshold(args: {
  threshold: Threshold;
}): Threshold {
  const { threshold } = args;

  const newThreshold = structuredClone(threshold);
  newThreshold.publicId = generatePublicId('threshold');

  return newThreshold;
}

function generateThresholdFromWorkflowState(args: {
  workflowState: {
    sourceAction: Action;
    workflowData: Partial<SchemaInstance>;
    workflowProps: WorkflowProps;
    featureFlagsForStore: WorkflowState['featureFlagsForStore'];
  };
}): Threshold {
  const {
    sourceAction, workflowData, workflowProps, featureFlagsForStore,
  } = args.workflowState;

  const leftAction = getDefaultAction({ sourceAction, workflowData });
  const leftAttribute = isActionOfType(leftAction, ActionType.SERVICE)
    ? getDefaultAttribute({ action: leftAction, workflowProps })
    : null;

  const dataType = getActionDataType({
    action: leftAction, attribute: leftAttribute,
  });

  const operator: string = (
    defaultOperatorByDataType[dataType]
    || defaultOperatorByActionType[sourceAction.type]
    || 'e'
  );

  const reasonCodeGroup = dataType === 'reasoncodes'
    ? getDefaultReasonCodeGroup(workflowProps.reasonCodeGroups)
    : null;

  const aggregationComponents = isAggregationServiceAction(leftAction) && leftAttribute
    ? resolveAggregationComponents(leftAttribute, workflowProps.aggregationConfig)
    : [];

  return {
    actionId: sourceAction.publicId,
    publicId: generatePublicId('threshold'),
    left: {
      actionId: leftAction.publicId,
      attributeId: leftAttribute?.attribute_id || null,
      attributeComponents: leftAttribute?.isMultilevel
        ? getDefaultMLAAttributeComponents(leftAttribute)
        : [],
      attributeToken: leftAttribute?.attribute_token,
      aggregationComponents,
    },
    operator,
    right: {
      actionId: null,
      attributeId: null,
      attributeToken: null,
      attributeComponents: [],
      reasonCodeGroupId: reasonCodeGroup?.id || null,
      reasonCodeGroup,
      attributeValue: getDefaultRightAttributeValue(
        dataType,
        featureFlagsForStore.allowBestEffortRuleEvaluations,
        leftAttribute,
      ),
      leftTagActionResult: isActionOfType(leftAction, ActionType.TAG) ? true : null,
      aggregationComponents: [],
    },
  };
}


export function generateThreshold(args: {
  fromWorkflowState: {
    sourceAction: Action;
    workflowData: Partial<SchemaInstance>;
    workflowProps: WorkflowProps;
    featureFlagsForStore: WorkflowState['featureFlagsForStore'];
  };
}): Threshold;


export function generateThreshold(args: {
  fromThreshold: Threshold;
}): Threshold;


export function generateThreshold(args: {
  fromWorkflowState?: {
    sourceAction: Action;
    workflowData: Partial<SchemaInstance>;
    workflowProps: WorkflowProps;
    featureFlagsForStore: WorkflowState['featureFlagsForStore'];
  };
  fromThreshold?: Threshold;
}): Threshold {
  const { fromWorkflowState, fromThreshold } = args;

  if (fromWorkflowState) {
    return generateThresholdFromWorkflowState({ workflowState: fromWorkflowState });
  }

  if (fromThreshold) {
    return generateThresholdFromThreshold({ threshold: fromThreshold });
  }

  return null;
}


export function createThresholdLeft(
  overrides: Partial<ThresholdLeft> = {},
): ThresholdLeft {
  return { ...emptyThresholdLeft, ...overrides };
}


export function createThresholdRight(
  overrides: Partial<ThresholdRight> = {},
): ThresholdRight {
  return { ...emptyThresholdRight, ...overrides };
}


export function createSnippetGateThreshold({
  parentActionId,
  ownerActionId,
}: {
  parentActionId: Action['publicId'];
  ownerActionId: Action['publicId'];
}): Threshold {
  return {
    publicId: generatePublicId('threshold'),
    actionId: ownerActionId,
    left: createThresholdLeft({ actionId: parentActionId }),
    operator: 'e',
    right: createThresholdRight({ leftTagActionResult: true }),
  };
}


export function isSnippetGateThreshold(args: {
  threshold: Threshold;
  parentSnippetActionPublicId: Action['publicId'];
}): boolean {
  return args.threshold.left.actionId === args.parentSnippetActionPublicId;
}


// Replaces updateThresholdValuesBasedOnDataType
export function updateThresholdSimpleValue({
  threshold,
  field,
  value,
}: {
  threshold: Threshold;
  field: string;
  value: string | number | boolean;
}): Threshold {
  const newThreshold = structuredClone(threshold);
  setProperty(newThreshold, field, value);

  return newThreshold;
}


// Replaces assignDefaultsToThreshold for left side updates
export function updateThresholdLeftSide({
  originalThreshold,
  leftActionId,
  leftAttributeId,
  workflowData,
  workflowProps,
  featureFlags,
}: {
  originalThreshold: Threshold;
  leftActionId: string;
  leftAttributeId?: number; // If provided, update attribute; if not, use default
  workflowData: SchemaInstance;
  workflowProps: WorkflowProps;
  featureFlags: WorkflowState['featureFlagsForStore'];
}): Threshold {
  const leftAction = workflowData.actions[leftActionId];

  const leftAttribute = getAttribute(leftAction, workflowProps, { attributeId: leftAttributeId });

  const dataType = getActionDataType({
    action: leftAction, attribute: leftAttribute,
  });

  const operator: string = (
    defaultOperatorByDataType[dataType]
    || defaultOperatorByActionType[leftAction.type]
    || 'e'
  );

  const reasonCodeGroup = dataType === 'reasoncodes'
    ? getDefaultReasonCodeGroup(workflowProps.reasonCodeGroups)
    : null;

  const aggregationComponents = isAggregationServiceAction(leftAction) && leftAttribute
    ? resolveAggregationComponents(
      leftAttribute,
      workflowProps.aggregationConfig,
      originalThreshold.left.aggregationComponents,
    )
    : [];

  return {
    ...originalThreshold,
    left: {
      actionId: leftAction.publicId,
      attributeId: leftAttribute?.attribute_id || null,
      attributeComponents: leftAttribute?.isMultilevel
        ? getDefaultMLAAttributeComponents(leftAttribute)
        : [],
      attributeToken: leftAttribute?.attribute_token,
      aggregationComponents,
    },
    operator,
    right: {
      // set the RHS defaults to match the new LHS data
      actionId: null,
      attributeId: null,
      attributeToken: null,
      attributeComponents: [],
      reasonCodeGroupId: reasonCodeGroup?.id || null,
      reasonCodeGroup,
      attributeValue: getDefaultRightAttributeValue(
        dataType,
        featureFlags.allowBestEffortRuleEvaluations,
        leftAttribute,
      ),
      aggregationComponents: [],
      leftTagActionResult: isActionOfType(leftAction, ActionType.TAG) ? true : null,
    },
  };
}

// Replaces assignDynamicComparisonRightSide for right side updates
export function updateThresholdRightAction({
  originalThreshold,
  rightActionId,
  workflowData,
  workflowProps,
}: {
  originalThreshold: Threshold;
  rightActionId: string;
  workflowData: SchemaInstance;
  workflowProps: WorkflowProps;
}): Threshold {
  const rightAction: Action = workflowData.actions[rightActionId];

  // Prevent matrix model actions from being used as right-hand side actions
  // Only validate when the right-hand side is being changed (not when loading existing data)
  if (rightActionId !== originalThreshold.right.actionId
    && isActionOfType(rightAction, [
      ActionType.MATRIX_MODEL, ActionType.IMPORTED_MATRIX_MODEL,
    ])) {
    throw new TypeError(
      `Matrix model actions cannot be used as right-hand side actions in thresholds. Action: ${rightAction.publicId}`,
    );
  }

  if (isActionOfType(rightAction, ActionType.SERVICE)) {
    return updateThresholdRightService({
      originalThreshold,
      rightAction,
      workflowData,
      workflowProps,
    });
  }

  return {
    ...originalThreshold,
    right: {
      actionId: rightAction.publicId,
      attributeId: null,
      attributeToken: null,
      attributeComponents: [],
      reasonCodeGroupId: null,
      reasonCodeGroup: null,
      attributeValue: null,
      leftTagActionResult: null,
      aggregationComponents: [],
    },
  };
}

export function updateThresholdRightService({
  originalThreshold,
  rightAction,
  rightAttributeId,
  workflowData,
  workflowProps,
}: {
  originalThreshold: Threshold;
  rightAction: Action;
  rightAttributeId?: number; // If provided, update attribute; if not, use default
  workflowData: SchemaInstance;
  workflowProps: WorkflowProps;
}): Threshold {
  // Get left attribute's data type for compatibility when no specific right attribute is requested
  let leftAttributeDataType = null;
  if (!rightAttributeId) {
    const leftAction = workflowData.actions[originalThreshold.left.actionId];
    const leftAttribute = isActionOfType(leftAction, ActionType.SERVICE)
      ? workflowProps.services[leftAction.service.internalName]?.attributes[
        originalThreshold.left.attributeId
      ]
      : null;

    leftAttributeDataType = getActionDataType({
      action: leftAction,
      attribute: leftAttribute,
    });
  }

  const rightAttribute = getAttribute(rightAction, workflowProps, {
    attributeId: rightAttributeId,
    dataType: leftAttributeDataType,
  });

  const aggregationComponents = isAggregationServiceAction(rightAction) && rightAttribute
    ? resolveAggregationComponents(
      rightAttribute,
      workflowProps.aggregationConfig,
      originalThreshold.right.aggregationComponents,
    )
    : [];

  return {
    ...originalThreshold,
    right: {
      actionId: rightAction.publicId,
      attributeId: rightAttribute?.attribute_id || null,
      attributeToken: rightAttribute?.attribute_token || null,
      attributeComponents: rightAttribute?.isMultilevel
        ? getDefaultMLAAttributeComponents(rightAttribute)
        : [],
      reasonCodeGroupId: null,
      reasonCodeGroup: null,
      attributeValue: null,
      leftTagActionResult: null,
      aggregationComponents,
    },
  };
}


export function assignDefaultsToThreshold({
  path,
  threshold: previousThreshold,
  workflowData,
  workflowProps,
  value,
  featureFlags,
}: {
  path: string;
  threshold: Threshold;
  workflowData: SchemaInstance;
  workflowProps: WorkflowProps;
  value: string | number;
  featureFlags: WorkflowState['featureFlagsForStore'];
}) {
  /**
   * Generate a new threshold object by retaining unchanged values from the original threshold
   * and assigning null values to fields that will be updated.
   */
  let threshold = {
    ...previousThreshold,
    left: {
      actionId: null,
      attributeId: null,
      attributeToken: null,
      attributeComponents: [],
      aggregationComponents: [],
    },
    operator: null,
    right: {
      actionId: null,
      attributeId: null,
      attributeToken: null,
      attributeComponents: [],
      reasonCodeGroupId: null,
      reasonCodeGroup: null,
      attributeValue: null,
      leftTagActionResult: null,
      aggregationComponents: [],
    },
  };
  const { workflow, services, reasonCodeGroups } = workflowProps;
  let leftAction = null;
  let leftAttribute = null;
  let leftAttributeDataType = 'decimal';
  let leftAttributeValues = [];

  if (path.includes('actionId')) {
    const leftActionUpdateMode = path === 'left.actionId';

    if (leftActionUpdateMode) {
      threshold.left.actionId = value;
      leftAction = workflowData.actions[value];
    } else {
      leftAction = threshold.left.actionId;
    }

    // Try to determine where the Attribute is coming from and what the data type of it is
    if (leftAction.type === ActionType.SERVICE) {
      // Attribute belongs to a Service, track it down in the services object.
      // But, only track down ACTIVE attributes!
      const { attributes } = services[leftAction.service.internalName];
      if (threshold.left.attributeId === null) {
        const activeAttributes: Record<string, Attribute> = Object.entries(attributes)
          .filter(([, attributeObj]) => !attributeObj.archivedAt)
          .reduce((acc, [attributeId, attributeObj]) => ({
            ...acc, [attributeId]: attributeObj,
          }), {});
        /*
          Temporarily hiding features for Portfolio Evaluations
          Epic ID: 109173
        */
        const allowedActiveAttributes = getAllowedAttributesForPortfolioWorkflow(
          workflow.type,
          services[leftAction.service.internalName]?.name,
          activeAttributes,
        );

        // Sort the attributes by name to get the alphabetically first attribute_id of the list
        threshold.left.attributeId = parseInt(
          Object.entries(allowedActiveAttributes)
            .sort(([, attrA], [, attrB]) => (
              (attrA as Attribute).display_name.localeCompare((attrB as Attribute).display_name)
            ))[0][0],
          10,
        );

        threshold.left.attributeToken = (Object.entries(allowedActiveAttributes)
          .sort(([, attrA], [, attrB]) => (
            (attrA as Attribute).display_name.localeCompare((attrB as Attribute).display_name)
          ))[0][1] as Attribute)?.attribute_token;
      }
      leftAttribute = attributes[threshold.left.attributeId];
      leftAttributeDataType = leftAttribute.dataType;
      if (leftAttribute.is_multilevel && leftAttribute.components) {
        const mlaComponents = transformMLAAttributeComponents({
          attributeFilterComponents: leftAttribute.components,
        });
        threshold.left.attributeComponents = assignDefaultsToMLAFilterComponents({
          mlaFilterComponents: mlaComponents,
        });
      }
      if (leftAttribute.values) {
        leftAttributeValues = leftAttribute.values.split(',');
      }
    } else if (leftAction.type === ActionType.MATRIX_MODEL) {
      /**
        * Matrix models have a `matrix_data_type` of either `string` or `number` but for threshold
        * purposes the `number` types will always be parsed as `decimal` on the back end so that
        * is how we set the `data_type` for the threshold here
        */
      leftAttributeDataType = leftAction.matrixModel.matrixDataType === MatrixModelDataType.STRING
        ? leftAction.matrixModel.matrixDataType
        : 'decimal';
    } else if (leftAction.type === ActionType.IMPORTED_MATRIX_MODEL) {
      /**
       * Imported matrix models do not have the same data types as matrix models created within
       * the current workflow (in else condition above).
       *
       * The `importedMatrixModel.dataType` will either be `string` or `decimal` so there is no
       * need to translate `number` to `decimal` like above.
       */
      leftAttributeDataType = leftAction.importedMatrixModel.dataType;
    } else if (leftAction.type === ActionType.TAG) {
      leftAttributeDataType = null;
      threshold.right.leftTagActionResult = true;
      // POST endpoint requires `operator` not to be `null`
      threshold.operator = 'lt';
    } else if (leftAction.type === ActionType.IMPORTED_OUTPUT_ATTRIBUTE) {
      leftAttributeDataType = leftAction.importedOutputAttribute.dataType;
    } else if (leftAction.type === ActionType.OUTCOME_REASON) {
      /**
       * Don't set a data type here so that imported outcome reasons are treated the same
       * as tags. Using the `dataType` from the API (`boolean` for `outcome_reason`) will
       * result in the `Threshold` component unnecessarily rendering a `BooleanDropdown`
       * for thresholding using outcomes reasons on the left side.
       */
      leftAttributeDataType = null;
      threshold.right.attributeValue = 'true';
      threshold.operator = 'e';
    } else if ([
      ActionType.INPUT,
      ActionType.OUTPUT,
    ].includes(leftAction.type) && leftAction[leftAction.type]?.dataType) {
      // Some actions have a dataType set within the action options
      // todo - confirm this is still needed. seems like leftovers from the legacy editor
      leftAttributeDataType = leftAction[leftAction.type].dataType;
    }
  } else if (path === 'left.attributeId') {
    /* The left attribute is being updated, set the requisite values
     */
    threshold.left.actionId = previousThreshold.left.actionId;
    leftAction = workflowData.actions[threshold.left.actionId];
    const { attributes } = services[leftAction.service.internalName];
    leftAttribute = attributes[value];
    threshold.left.attributeId = value;
    threshold.left.attributeToken = leftAttribute.attribute_token;
    leftAttributeDataType = leftAttribute.dataType;
    leftAttributeValues = leftAttribute.values;

    if (leftAttribute.is_multilevel) {
      const mlaComponents = transformMLAAttributeComponents({
        attributeFilterComponents: leftAttribute.components,
      });
      threshold.left.attributeComponents = assignDefaultsToMLAFilterComponents({
        mlaFilterComponents: mlaComponents,
      });
    }

    if (leftAttribute.values?.includes(',')) {
      leftAttributeValues = leftAttribute.values.split(',');
    }
  }

  // Assign default values to new threshold based on service attribute type
  if (leftAttributeDataType) {
    threshold = {
      ...threshold,
      ...getOperatorAndRightAttrValues({
        leftAttribute,
        leftAttributeDataType,
        leftAttributeValues,
        leftAction,
        reasonCodeGroups,
        featureFlags,
      }),
    };
  }

  return threshold;
}

export function updateThresholdValuesBasedOnDataType({
  path,
  threshold,
  workflowData,
  workflowProps,
  value,
}: {
  path: string;
  threshold: Threshold;
  workflowData: SchemaInstance;
  workflowProps: WorkflowProps;
  value: string | number;
}) {
  const updatedThreshold = { ...threshold };
  setProperty(updatedThreshold, path, value);

  const { services } = workflowProps;
  let leftAction = null;
  let leftAttribute = null;
  let leftAttributeDataType = 'decimal';

  leftAction = workflowData.actions[threshold.left.actionId];

  if (leftAction.type === ActionType.SERVICE) {
    const { attributes } = services[leftAction.service.internalName];
    leftAttribute = attributes[threshold.left.attributeId];
    leftAttributeDataType = leftAttribute.dataType;
  } else if (leftAction.type === ActionType.MATRIX_MODEL) {
    leftAttributeDataType = leftAction.matrixModel.matrixDataType === MatrixModelDataType.STRING
      ? leftAction.matrixModel.matrixDataType
      : 'decimal';
  } else if (leftAction.type === ActionType.IMPORTED_MATRIX_MODEL) {
    leftAttributeDataType = getFormattedMatrixDataType(leftAction.importedMatrixModel.dataType);
  } else if (leftAction.type === ActionType.TAG || leftAction.type === ActionType.OUTCOME_REASON) {
    leftAttributeDataType = null;
  } else if (leftAction.type === ActionType.IMPORTED_OUTPUT_ATTRIBUTE) {
    leftAttributeDataType = leftAction.importedOutputAttribute.dataType;
  } else if ([
    ActionType.INPUT,
    ActionType.OUTPUT,
  ].includes(leftAction.type) && leftAction[leftAction.type]?.dataType) {
    leftAttributeDataType = leftAction[leftAction.type].dataType;
  }

  if (leftAttributeDataType === 'boolean' && updatedThreshold.operator !== 'e') {
    updatedThreshold.operator = 'e';
  } else if (leftAttributeDataType === 'string' && ['lt', 'le', 'gt', 'ge'].includes(updatedThreshold.operator)) {
    // those lt/le/gt/ge can't be performed on string matching operators
    updatedThreshold.operator = 'e';
  }

  if (path === 'right.attributeId') {
    const rightAction = workflowData.actions[threshold.right.actionId];
    const { attributes } = services[rightAction.service.internalName];
    const rightAttribute = attributes[value];
    if (rightAttribute.is_multilevel) {
      const mlaComponents = transformMLAAttributeComponents({
        attributeFilterComponents: rightAttribute.components,
      });
      threshold.right.attributeComponents = assignDefaultsToMLAFilterComponents({
        mlaFilterComponents: mlaComponents,
      });
    }
    if (isAggregationServiceAction(rightAction)) {
      updatedThreshold.right.aggregationComponents = resolveAggregationComponents(
        rightAttribute,
        workflowProps.aggregationConfig,
        threshold.right.aggregationComponents,
      );
    }
  }
  return updatedThreshold;
}

export function modifyRelevantDataFromThresholdRemoval({
  thresholdId,
  workflowData,
}: {
  thresholdId: string;
  workflowData: Partial<SchemaInstance>;
}) {
  let targetActionIdWhereThresholdBelongTo;
  Object.values(workflowData.actions).every(action => {
    const thresholdIdsForAction = extractThresholdIds(action.rules);
    if (thresholdIdsForAction.find(id => id === thresholdId)) {
      targetActionIdWhereThresholdBelongTo = action.publicId;
      return false;
    }

    return true;
  });

  if (targetActionIdWhereThresholdBelongTo) {
    const targetAction = workflowData.actions[targetActionIdWhereThresholdBelongTo];
    const updatedRule = deleteThresholdFromBooleanRule({
      thresholdId,
      originalRule: targetAction.rules || {},
    });

    workflowData.actions[targetActionIdWhereThresholdBelongTo].rules = updatedRule;
  }
}

export function updateBooleanRuleWithNewThreshold({
  eventValue,
  thresholdId,
  workflowData,
}) {
  const thresholdTargetActionId = eventValue.actionId;
  const targetAction = workflowData.actions[thresholdTargetActionId];
  const updatedRule = addThresholdToBooleanRule({
    thresholdId,
    pathToPerformActionAt: '',
    originalRule: targetAction.rules || {},
  });

  workflowData.actions[thresholdTargetActionId].rules = updatedRule;
}

/**
 * Finds the first eligible action for dynamic comparison in a threshold.
 *
 * This function determines which actions can be used as right-hand side actions
 * based on the left-hand side action type and data type compatibility.
 *
 * @param threshold - The threshold containing the left-hand side action
 * @param actions - All available actions in the workflow
 * @param workflow - Workflow information including type
 * @param services - Available services for service action validation
 * @param actionIdsConnectedDownstreamFromThresholdOwner - Set of action IDs that would create cycles
 * @returns The first eligible action for dynamic comparison, or null if none found
 *
 * Eligibility rules:
 * - Left = INPUT → Right can be INPUT or SERVICE
 * - Left = OUTPUT/IMPORTED_OUTPUT_ATTRIBUTE → Right can only be SERVICE
 * - Left = SERVICE → Right can be INPUT or SERVICE
 * - Matrix models are not included in the eligibility criteria
 */
export function getFirstEligibleActionForDynamicComparison({
  threshold: previousThreshold,
  actions,
  workflow,
  services,
  actionIdsConnectedDownstreamFromThresholdOwner,
}: {
  threshold: Threshold;
  actions: Record<string, Action>;
  workflow: WorkflowInfo;
  services: ServicesByName;
  actionIdsConnectedDownstreamFromThresholdOwner: Set<string>;
}) {
  let availableAttributesOfFirstEligibleAction: Record<string, Attribute>;
  let leftAttribute = null;
  let leftAttributeDataType = null;

  const leftAction = actions[previousThreshold.left.actionId];
  if (leftAction.type === ActionType.SERVICE) {
    const { attributes: leftActionAttributes } = services[leftAction.service.internalName];
    leftAttribute = leftActionAttributes[previousThreshold.left.attributeId];
    leftAttributeDataType = leftAttribute?.dataType;
  } else if (leftAction.type === ActionType.INPUT) {
    leftAttributeDataType = leftAction.input.dataType;
  } else if (leftAction.type === ActionType.OUTPUT) {
    leftAttributeDataType = leftAction.output.dataType;
  } else if (leftAction.type === ActionType.MATRIX_MODEL) {
    leftAttributeDataType = leftAction.matrixModel.matrixDataType === MatrixModelDataType.STRING
      ? leftAction.matrixModel.matrixDataType
      : 'decimal';
  } else if (leftAction.type === ActionType.IMPORTED_MATRIX_MODEL) {
    leftAttributeDataType = leftAction.importedMatrixModel.dataType;
  } else if (leftAction.type === ActionType.IMPORTED_OUTPUT_ATTRIBUTE) {
    leftAttributeDataType = leftAction.importedOutputAttribute.dataType;
  }

  if (!leftAttributeDataType) return null;
  return Object.values(actions)
    // we filter out downstream actions to ensure no cycles are created in the graph
    .filter(action => !actionIdsConnectedDownstreamFromThresholdOwner.has(action.publicId))
    .find(action => {
      let actionTypeMatchingCriteria = false;
      if (leftAction.type === ActionType.INPUT) {
        actionTypeMatchingCriteria = [
          ActionType.INPUT,
          ActionType.SERVICE,
        ].includes(action.type);
      }

      if ([
        ActionType.IMPORTED_OUTPUT_ATTRIBUTE,
      ].includes(leftAction.type)) {
        actionTypeMatchingCriteria = action.type === ActionType.SERVICE;
      }

      if (leftAction.type === ActionType.SERVICE) {
        actionTypeMatchingCriteria = [
          ActionType.INPUT,
          ActionType.SERVICE,
        ].includes(action.type);
      }

      if (actionTypeMatchingCriteria) {
        if (action.type === ActionType.SERVICE) {
          availableAttributesOfFirstEligibleAction = getAllowedAttributesWithMatchingDataType({
            service: services[action.service.internalName] as ServiceObject,
            incomingDataType: leftAttributeDataType,
            workflowType: workflow.type,
          });

          if (Object.values(availableAttributesOfFirstEligibleAction).length > 0) {
            return action;
          }

          return null;
        }

        if (action.type === ActionType.INPUT
          && leftAction.type === ActionType.INPUT
          && leftAttributeDataType !== action.input.dataType
        ) {
          return null;
        }

        return action;
      }
      return null;
    });
}

export function assignDynamicComparisonRightSide({
  threshold: previousThreshold,
  rightActionId,
  workflowData,
  workflowProps,
  actionIdsConnectedDownstreamFromThresholdOwner,
}: {
  threshold: Threshold;
  workflowData: Partial<SchemaInstance>;
  workflowProps: WorkflowProps;
  rightActionId?: string;
  actionIdsConnectedDownstreamFromThresholdOwner?: Set<string>;
}): Threshold {
  // these are mutually exclusive. throwing here enforces fail-fast behavior
  if (rightActionId && actionIdsConnectedDownstreamFromThresholdOwner) {
    throw new TypeError('Cannot pass both parameters to function');
  }

  /**
   * Generate a new threshold object by retaining unchanged values from the original threshold
   * and assigning null values to fields that will be updated.
   */
  const threshold: Threshold = {
    ...previousThreshold,
    right: {
      actionId: rightActionId || null,
      attributeId: null,
      attributeToken: null,
      attributeComponents: [],
      reasonCodeGroupId: null,
      reasonCodeGroup: null,
      attributeValue: null,
      leftTagActionResult: null,
      aggregationComponents: [],
    },
  };
  const { workflow, services } = workflowProps;

  let leftAttribute = null;
  let leftAttributeDataType = 'decimal';

  const leftAction = workflowData.actions[previousThreshold.left.actionId];
  if (leftAction.type === ActionType.SERVICE) {
    const { attributes: leftActionAttributes } = services[leftAction.service.internalName];
    leftAttribute = leftActionAttributes[previousThreshold.left.attributeId];
    leftAttributeDataType = leftAttribute.dataType;
  } else if (leftAction.type === ActionType.INPUT) {
    leftAttributeDataType = leftAction.input.dataType;
  } else if (leftAction.type === ActionType.OUTPUT) {
    leftAttributeDataType = leftAction.output.dataType;
  } else if (leftAction.type === ActionType.MATRIX_MODEL) {
    leftAttributeDataType = leftAction.matrixModel.matrixDataType === MatrixModelDataType.STRING
      ? leftAction.matrixModel.matrixDataType
      : 'decimal';
  } else if (leftAction.type === ActionType.IMPORTED_MATRIX_MODEL) {
    leftAttributeDataType = leftAction.importedMatrixModel.dataType;
  } else if (leftAction.type === ActionType.IMPORTED_OUTPUT_ATTRIBUTE) {
    leftAttributeDataType = leftAction.importedOutputAttribute.dataType;
  }

  let availableAttributesOfFirstEligibleAction: Record<string, Attribute>;
  let firstEligibleAction: Action;
  if (rightActionId) {
    firstEligibleAction = workflowData.actions[rightActionId];

    // Prevent matrix model actions from being used as right-hand side actions
    // Only validate when the right-hand side is being changed (not when loading existing data)
    if (rightActionId !== previousThreshold.right.actionId
      && isActionOfType(firstEligibleAction, [
        ActionType.MATRIX_MODEL, ActionType.IMPORTED_MATRIX_MODEL,
      ])) {
      throw new TypeError(
        `Matrix model actions cannot be used as right-hand side actions in thresholds. Action: ${firstEligibleAction.publicId}`,
      );
    }
  } else {
    firstEligibleAction = getFirstEligibleActionForDynamicComparison({
      threshold,
      actions: workflowData.actions,
      workflow,
      services,
      actionIdsConnectedDownstreamFromThresholdOwner,
    });
  }

  if (firstEligibleAction?.type === ActionType.SERVICE) {
    availableAttributesOfFirstEligibleAction = getAllowedAttributesWithMatchingDataType({
      service: services[firstEligibleAction.service.internalName],
      incomingDataType: leftAttributeDataType,
      workflowType: workflow.type,
    });
  }

  threshold.right.reasonCodeGroupId = null;
  threshold.right.attributeValue = null;
  if (!rightActionId) threshold.right.actionId = firstEligibleAction.publicId;

  if (isActionOfType(firstEligibleAction, ActionType.SERVICE)) {
    const { attributes } = services[firstEligibleAction.service.internalName];
    // Sort the attributes by name to get the alphabetically first attribute_id of the list
    const sortedAttributesByName = Object.values(availableAttributesOfFirstEligibleAction)
      .sort((attrA, attrB) => (
        (attrA as Attribute).display_name.localeCompare((attrB as Attribute).display_name)
      ));
    threshold.right.attributeId = sortedAttributesByName[0]?.attribute_id ?? null;
    threshold.right.attributeToken = sortedAttributesByName[0]?.attribute_token ?? null;

    if (threshold.right.attributeId) {
      const rightAttribute = attributes[threshold.right.attributeId];
      if (rightAttribute.is_multilevel) {
        const mlaComponents = transformMLAAttributeComponents({
          attributeFilterComponents: rightAttribute.components,
        });
        threshold.right.attributeComponents = assignDefaultsToMLAFilterComponents({
          mlaFilterComponents: mlaComponents,
        });
      }
      if (isAggregationServiceAction(firstEligibleAction)) {
        threshold.right.aggregationComponents = resolveAggregationComponents(
          rightAttribute,
          workflowProps.aggregationConfig,
          previousThreshold.right.aggregationComponents,
        );
      }
    }
  }

  return threshold;
}


export function removeDynamicComparisonRightSide({
  threshold: previousThreshold,
  workflowData,
  workflowProps,
  featureFlags,
}: {
  threshold: Threshold;
  workflowData: Partial<SchemaInstance>;
  workflowProps: WorkflowProps;
  featureFlags: WorkflowState['featureFlagsForStore'];
}) {
  /**
   * Generate a new threshold object by retaining unchanged values from the original threshold
   * and assigning null values to fields that will be updated.
   */
  let threshold = {
    ...previousThreshold,
    right: {
      actionId: null,
      attributeId: null,
      attributeToken: null,
      attributeComponents: [],
      reasonCodeGroupId: null,
      reasonCodeGroup: null,
      attributeValue: null,
      leftTagActionResult: null,
      aggregationComponents: [],
    },
  };
  const { services, reasonCodeGroups } = workflowProps;
  const leftAction = workflowData.actions[previousThreshold.left.actionId];
  let leftAttribute = null;
  let leftAttributeDataType = 'decimal';
  let leftAttributeValues = [];

  if (leftAction.type === ActionType.SERVICE) {
    const { attributes: leftActionAttributes } = services[leftAction.service.internalName];
    leftAttribute = leftActionAttributes[previousThreshold.left.attributeId];
    leftAttributeDataType = leftAttribute.dataType;
  } else if (leftAction.type === ActionType.IMPORTED_MATRIX_MODEL) {
    leftAttributeDataType = leftAction.importedMatrixModel.dataType;
  } else if (leftAction.type === ActionType.TAG) {
    threshold.right.leftTagActionResult = true;
  } else if (leftAction.type === ActionType.IMPORTED_OUTPUT_ATTRIBUTE) {
    leftAttributeDataType = leftAction.importedOutputAttribute.dataType;
  } else if (leftAction.type === ActionType.OUTCOME_REASON) {
    /**
     * Don't set a data type here so that imported outcome reasons are treated the same
     * as tags. Using the `dataType` from the API (`boolean` for `outcome_reason`) will
     * result in the `Threshold` component unnecessarily rendering a `BooleanDropdown`
     * for thresholding using outcomes reasons on the left side.
     */
    threshold.right.leftTagActionResult = true;
    threshold.operator = 'lt';
  } else if ([
    ActionType.INPUT,
    ActionType.OUTPUT,
  ].includes(leftAction.type) && leftAction[leftAction.type]?.dataType) {
    // Some actions have a dataType set within the action options
    // todo - confirm this is still needed. seems like leftovers from the legacy editor
    leftAttributeDataType = leftAction[leftAction.type].dataType;
  }

  if (leftAttribute?.is_multilevel) {
    const mlaComponents = transformMLAAttributeComponents({
      attributeFilterComponents: leftAttribute.components,
    });
    threshold.left.attributeComponents = assignDefaultsToMLAFilterComponents({
      mlaFilterComponents: mlaComponents,
    });
  }
  if (isAggregationServiceAction(leftAction) && leftAttribute) {
    threshold.left.aggregationComponents = resolveAggregationComponents(
      leftAttribute, workflowProps.aggregationConfig,
      threshold.left.aggregationComponents,
    );
  }
  if (leftAttribute?.values) {
    leftAttributeValues = leftAttribute.values.split(',');
  }

  const { right: rightThreshold } = getOperatorAndRightAttrValues({
    leftAttribute,
    leftAttributeDataType,
    leftAttributeValues,
    leftAction,
    reasonCodeGroups,
    featureFlags,
  });
  // Assign default values to new threshold based on service attribute type
  if (leftAttributeDataType) {
    threshold = {
      ...threshold,
      right: rightThreshold,
    };
  }

  return threshold;
}
